export type StableId = string

declare const answerKeyBrand: unique symbol
export type AnswerKey = string & { readonly [answerKeyBrand]: true }

export type DraftAnswerValue = string | readonly string[]
export type DraftAnswerMap = Readonly<Record<AnswerKey, DraftAnswerValue>>

export type AnswerSlot =
  | 'question'
  | 'blank'
  | 'paragraph'
  | 'statement'
  | 'sentence'
  | 'picture-item'
  | 'media'
  | 'notes'

export interface ParsedAnswerKey {
  questionId: StableId
  slot: AnswerSlot
  itemId?: StableId
}

function requireStableId(value: string, label: string): string {
  const id = value.trim()
  if (!id) throw new Error(`${label} must be a non-empty stable ID`)
  return encodeURIComponent(id)
}

function compositeKey(questionId: StableId, slot: AnswerSlot, itemId?: StableId): AnswerKey {
  const question = requireStableId(questionId, 'questionId')
  if (itemId === undefined) return `question:${question}:${slot}` as AnswerKey
  return `question:${question}:${slot}:${requireStableId(itemId, 'itemId')}` as AnswerKey
}

export const answerKeys = {
  question: (questionId: StableId) => compositeKey(questionId, 'question'),
  blank: (questionId: StableId, blankId: StableId) => compositeKey(questionId, 'blank', blankId),
  paragraph: (questionId: StableId, paragraphId: StableId) => compositeKey(questionId, 'paragraph', paragraphId),
  statement: (questionId: StableId, statementId: StableId) => compositeKey(questionId, 'statement', statementId),
  sentence: (questionId: StableId, sentenceId: StableId) => compositeKey(questionId, 'sentence', sentenceId),
  pictureItem: (questionId: StableId, itemId: StableId) => compositeKey(questionId, 'picture-item', itemId),
  media: (questionId: StableId) => compositeKey(questionId, 'media'),
  notes: (questionId: StableId) => compositeKey(questionId, 'notes'),
} as const

const SIMPLE_SLOTS = new Set<AnswerSlot>(['question', 'media', 'notes'])
const ITEM_SLOTS = new Set<AnswerSlot>(['blank', 'paragraph', 'statement', 'sentence', 'picture-item'])

export function parseAnswerKey(value: string): ParsedAnswerKey | null {
  const parts = value.split(':')
  if (parts[0] !== 'question' || parts.length < 3 || parts.length > 4) return null

  const slot = parts[2] as AnswerSlot
  try {
    if (SIMPLE_SLOTS.has(slot) && parts.length === 3) {
      return { questionId: decodeURIComponent(parts[1]), slot }
    }
    if (ITEM_SLOTS.has(slot) && parts.length === 4) {
      return { questionId: decodeURIComponent(parts[1]), slot, itemId: decodeURIComponent(parts[3]) }
    }
  } catch {
    return null
  }
  return null
}

export function setDraftAnswer(
  answers: DraftAnswerMap,
  key: AnswerKey,
  value: DraftAnswerValue,
): DraftAnswerMap {
  return { ...answers, [key]: value }
}

export function getDraftAnswer(answers: DraftAnswerMap, key: AnswerKey): DraftAnswerValue | undefined {
  return answers[key]
}

export function isMeaningfulAnswer(value: DraftAnswerValue | undefined): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  return Array.isArray(value) && value.some((item) => item.trim().length > 0)
}
